import React from "react";
import Tooltip from "@mui/material/Tooltip";
import CircleIcon from "@mui/icons-material/Circle";

/**
 * TE-2011: a coloured status dot with a hover detail, used by the Asset
 * Tracking and Heartbeat Tracking columns on the Lift Details grid.
 *
 * Takes the object returned by assetTrackingIndicator / heartbeatIndicator so
 * the colour rules stay in liftIndicators.js and this stays presentational.
 *
 * The dot carries meaning, so it cannot be colour-only: the tooltip is exposed
 * as the element's accessible name and the indicator state is also published
 * through the column's valueGetter, which is what quick search, the column
 * filter and CSV export read.
 *
 * TE-2066: CircleIcon previously also received `titleAccess={tooltip}`, which
 * renders an SVG <title> element. Browsers show that as a NATIVE tooltip after
 * a hover delay, so holding the pointer on a dot produced a second, grey bubble
 * underneath the MUI one. It is removed. Accessibility is unaffected — the
 * accessible name still comes from aria-label on the wrapping span, which is
 * what screen readers announce and what the tests assert on.
 *
 * `maxWidth: "none"` with `whiteSpace: "pre"` keeps each tooltip line on one
 * row. The default 260px cap wrapped the timestamp lines mid-value, which is
 * what PBC reported as the bubble needing resizing.
 *
 * @param {{state: string, color: ?string, label: string, tooltip: string}} indicator
 */
function IndicatorCell({ indicator }) {
  if (!indicator) {
    return null;
  }

  const { color, label, tooltip, state } = indicator;

  return (
    <Tooltip
      title={tooltip}
      arrow
      placement="top"
      componentsProps={{
        tooltip: { sx: { whiteSpace: "pre", fontSize: "12px", maxWidth: "none" } },
      }}
    >
      <span
        data-testid="indicator-cell"
        data-state={state}
        aria-label={tooltip}
        style={{ display: "inline-flex", alignItems: "center", cursor: "default" }}
      >
        {color ? (
          <CircleIcon style={{ width: "10px", color }} />
        ) : (
          <span style={{ fontSize: "13px" }}>{label}</span>
        )}
      </span>
    </Tooltip>
  );
}

export default IndicatorCell;
